import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { UILoader } from '@futrli/ui-components';
import { workspaceByIdSelector } from '@futrli/shared-services';
import { WorkspacesListingItem } from '@futrli-sdk/workspaces';
import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';

import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { StoreState } from '../../../../core/types';
import { UserGridSettingsContext } from '../../reports/user-grid-settings/user-grid-settings-context';
import { getCashFlowStatementConsolidationAccountCategories } from '../util-fns/consolidation-util-fns';
import { PredictionsTotalsPageWrapper } from '../predictions-totals-page-wrapper';
import { useConsolidationDataContext } from '../../../../predict-management/context/consolidation-data-context';
import { ForecastUpdating } from '../../components/forecast-updating';

export const ConsolidationCashFlowStatement = () => {
  const { workspaceId: tempWorkspaceId } = useParams();
  const workspaceId = tempWorkspaceId || '';

  const intl = useIntl();

  const { consolidatedForecastReport, isLoading, totalCashPredictRow, forecast } = usePredictionsInfoContext();

  const workspace = useSelector((state: StoreState) => workspaceByIdSelector(state.normalised, workspaceId));

  const { nodeStaticIds } = useLocalisedConstantsContext();

  const { consolidation } = useConsolidationDataContext();

  const { expandedRows, getShowZeroValueAccounts } = useContext(UserGridSettingsContext);

  const dataGridRows = useMemo(() => {
    const rows = getCashFlowStatementConsolidationAccountCategories(
      intl,
      nodeStaticIds,
      workspace as WorkspacesListingItem,
      expandedRows,
      consolidatedForecastReport,
      consolidation,
      getShowZeroValueAccounts(forecast?.id || '')
    );

    return rows;
  }, [
    intl,
    nodeStaticIds,
    workspace,
    expandedRows,
    consolidatedForecastReport,
    consolidation,
    getShowZeroValueAccounts,
    forecast?.id
  ]);

  if (!consolidatedForecastReport && isLoading) {
    return <UILoader isFull />;
  }

  return (
    <>
      <PredictionsTotalsPageWrapper
        columns={consolidatedForecastReport?.data.cash_flow_columns}
        totalAccountRows={dataGridRows}
        totalCashPredictRow={totalCashPredictRow}
        isCashFlow
      />
      <ForecastUpdating show={!!isLoading} />
    </>
  );
};
